import React from "react";
import styled from "@emotion/styled";
import { NavLink, useNavigate } from "react-router-dom";

import { green } from "@mui/material/colors";

import { Box, Chip, Drawer as MuiDrawer, ListItemButton, Stepper, Step, StepLabel } from "@mui/material";

import { ReactComponent as Logo } from "@/vendor/logo.svg";
import { SidebarItemsType } from "@/types/sidebar";
import Footer from "./SidebarFooter";
import SidebarNav from "./SidebarNav";

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    scrollbar-width: none;
  }
`;

const Brand = styled(ListItemButton)<{
  component?: React.ReactNode;
  to?: string;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${(props) => props.theme.sidebar.header.background};
  }
`;

const BrandIcon = styled(Logo)`
  margin-right: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  width: 32px;
  height: 32px;
`;

const BrandChip = styled(Chip)`
  background-color: ${green[700]};
  border-radius: 5px;
  color: ${(props) => props.theme.palette.common.white};
  font-size: 55%;
  height: 18px;
  margin-left: 2px;
  margin-top: -16px;
  padding: 3px 0;

  span {
    padding-left: ${(props) => props.theme.spacing(1.375)};
    padding-right: ${(props) => props.theme.spacing(1.375)};
  }
`;

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  steps: SidebarItemsType[];
  currentStep?: number;
  showFooter?: boolean;
  sx?: any;
};

function CircleStepIcon() {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #a5bdd1',
      }}
    >
      <Box 
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          display: 'inline-block',
        }}
      />
    </Box>
  );
}

const StepperSidebar: React.FC<SidebarProps> = ({
  steps,
  showFooter = true,
  currentStep,
  variant = "permanent",
  sx,
  ...rest
}) => {
  const navigate = useNavigate();
  return (
    <Drawer
      variant={variant}
      {...rest}
      sx={{
        '& .MuiDrawer-paper': {
          background: "#F7F9FC",
          color: "rgba(0,0,0,0.87)",
          flex: 1,
        },
        ...(sx || {}),
      }}
    >
      <Brand component={NavLink as any} to="/">
        <div style={{textAlign: "left", width: "100%"}}>
          <svg className="app-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1052.35 462.81" style={{width: '90px'}} fill={'#fff'}>
            <defs></defs><g id="Layer_2" data-name="Layer 2"><g id="Layer_1-2" data-name="Layer 1"><path d="M651.88 318.18A11 11 0 0 1 641 307.25V177.9L575.9 312a12.12 12.12 0 0 1-4.22 4.47 10.25 10.25 0 0 1-11.42 0A12.12 12.12 0 0 1 556 312l-65-134.1v129.35a10.72 10.72 0 0 1-3.1 7.7 10.39 10.39 0 0 1-7.82 3.23 11 11 0 0 1-10.92-10.92v-174a11 11 0 0 1 20.86-5l76 154.18 76-154.18a11 11 0 0 1 20.86 5v174a11 11 0 0 1-10.92 10.92zm66.79-144.75A14.84 14.84 0 0 1 704 158.78a13.56 13.56 0 0 1 4.34-10.3 15.16 15.16 0 0 1 20.73 0 13.76 13.76 0 0 1 4.22 10.3 14.84 14.84 0 0 1-14.65 14.65zm0 144.75a11 11 0 0 1-10.92-10.92v-106a11 11 0 0 1 10.92-10.92 10.92 10.92 0 0 1 7.82 3.23 10.32 10.32 0 0 1 3.35 7.7v106a10.32 10.32 0 0 1-3.35 7.7 10.92 10.92 0 0 1-7.83 3.21zm113.71-105.77h-20.11v94.84a10.51 10.51 0 0 1-3.23 7.7 10.8 10.8 0 0 1-7.94 3.23 11 11 0 0 1-10.92-10.92v-94.85h-20.37a11 11 0 0 1-10.92-10.92 10.8 10.8 0 0 1 3.23-7.94 10.51 10.51 0 0 1 7.7-3.23h20.36v-36.5a10.39 10.39 0 0 1 3.22-7.82 10.72 10.72 0 0 1 7.7-3.1 11 11 0 0 1 7.9 3.1 10.39 10.39 0 0 1 3.23 7.82v36.5h20.11a11 11 0 0 1 11.17 11.17 10.51 10.51 0 0 1-3.23 7.7 10.8 10.8 0 0 1-7.9 3.22zm94.59 0h-20.11v94.84a10.51 10.51 0 0 1-3.23 7.7 10.8 10.8 0 0 1-7.95 3.23 11 11 0 0 1-10.92-10.92v-94.85H864.4a11 11 0 0 1-10.92-10.92 10.8 10.8 0 0 1 3.23-7.94 10.51 10.51 0 0 1 7.7-3.23h20.36v-36.5A10.39 10.39 0 0 1 888 146a10.72 10.72 0 0 1 7.7-3.1 11 11 0 0 1 7.95 3.1 10.39 10.39 0 0 1 3.23 7.82v36.5H927a11 11 0 0 1 11.17 11.17 10.51 10.51 0 0 1-3.23 7.7 10.8 10.8 0 0 1-7.94 3.22zm124.39-7l-41.46 102.83-21.6 52.64a10.6 10.6 0 0 1-4 5.09 10.76 10.76 0 0 1-6.21 1.86 11 11 0 0 1-10.92-10.92 11.92 11.92 0 0 1 .74-4.22L987.8 304l-39.72-98.57a10.7 10.7 0 0 1-.74-4.22 11 11 0 0 1 10.92-10.92 11.35 11.35 0 0 1 6.33 1.86 10.37 10.37 0 0 1 4.1 5.09l31 79.45 31.31-79.43a10.6 10.6 0 0 1 4-5.09 11.34 11.34 0 0 1 14.15 1.37 10.51 10.51 0 0 1 3.23 7.7 8.38 8.38 0 0 1-1.02 4.22zM303.67 369.28l-71.36 15.5a3.6 3.6 0 0 0-2.78 2.81l.05-.57c.34-3.72-3-7.1-7.36-7.5l-41.93-3.85c-4.39-.4-8.26 2.31-8.6 6l-.05.57a3.6 3.6 0 0 0-2.22-3.27l-67.33-28.25c-7.49-2.68-14 .52-14.82 7.43 0 0 .16 27.41-.67 36.47-.91 9.9-6.3 39.38-6.3 39.38-.42 6.92 5.15 10.7 12.73 8.71l72.76-23a4 4 0 0 0 2.69-3l-.05.56c-.34 3.73 3 7.1 7.36 7.5l41.93 3.85c4.39.4 8.26-2.31 8.6-6l.05-.56a4 4 0 0 0 2.09 3.48l67.35 35.9c7.09 3.33 13.25.64 14.1-6.25 0 0 .07-30 1-39.87.83-9.06 6-36 6-36 .4-6.98-5.39-11.32-13.24-10.04zM355.15 48.64H301c-1.36-5.46-3.18-11.37-6.37-15.92-1.82-3.18-7.28-9.55-16.37-15.46-3.18-2.27-8.64-4.55-17.28-4.09a13.08 13.08 0 0 1 .45 14.55 12.75 12.75 0 0 1-15 4.55 43 43 0 0 0-12.73-21.83C218.27-3.65 197.35.44 195.08.9c.91.91 7.73 8.64 5.46 18.64C198.72 28.18 191 35 181 36.37A27 27 0 0 0 175.53 20c-9.09-10.91-24.1-9.09-25-9.09 1.36 2.27 5 10 2.27 18.64-2.73 6.82-8.64 10.46-12.28 12.73a47.24 47.24 0 0 1-20.92 6.37H54.11C24.56 48.64 0 73.2 0 103.21v161c0 29.56 24.56 54.11 54.11 54.11h300.58c30 0 54.57-24.56 54.57-54.11v-161c0-30.01-24.56-54.57-54.11-54.57zM101.86 169.41A21.16 21.16 0 1 1 123 148.24a21.23 21.23 0 0 1-21.14 21.17zm169.81 80.45c-42.91 21.4-91.15 21.4-134.06 0a9.72 9.72 0 0 1-4.5-12c1.74-4.37 6.23-6.14 10.07-4.22 39.35 19.62 83.58 19.62 122.93 0 3.85-1.91 8.33-.14 10.07 4.22a9.72 9.72 0 0 1-4.52 12zm35.83-81.18a21.16 21.16 0 1 1 21.16-21.16 21.23 21.23 0 0 1-21.17 21.16z" className="cls-1"></path></g></g>
          </svg>
        </div>
      </Brand>
      <Box p={2}>
        <Stepper activeStep={currentStep} orientation="vertical" nonLinear>
          {steps.map((step) => (
              <Step key={step.title} onClick={() => navigate(step.href)}>
                  <StepLabel slots={{ stepIcon: CircleStepIcon }} style={{cursor: "pointer"}}>{step.title}</StepLabel>
              </Step>
          ))}
        </Stepper>
      </Box>
      {/* {!!showFooter && <Footer />} */}
    </Drawer>
  );
};

export default StepperSidebar;
